// Why the browser can't reach the Dhaba Print Service on this computer.
//
// A failed request to http://127.0.0.1:38445 looks the same to the page whether
// the service is not running, Chrome's "Local network access" permission is
// blocked, or the browser can't talk to local services at all: fetch rejects
// with "TypeError: Failed to fetch". The permission state and the browser tell
// the cases apart well enough to give the right advice.

/** Chrome 145+ splits the permission; 142–144 use "local-network-access" (kept as an alias later). */
const LOOPBACK_PERMISSION_NAMES = ["loopback-network", "local-network-access"] as const;

export type LoopbackPermission = "granted" | "denied" | "prompt" | "unknown";

export type TpsProblem =
  /** Safari / iPhone / iPad: can't use the print service. */
  | "unsupported-browser"
  /** A site other than billing.dhaba.ai: the release print service refuses it. */
  | "wrong-origin"
  /** Chrome/Edge blocked this site from local devices. */
  | "permission-denied"
  /** The browser will ask (or is asking) for local network access. */
  | "permission-prompt"
  /** Nothing answered: the service is not installed or not running. */
  | "not-running";

function userAgent(): string {
  return typeof navigator === "undefined" ? "" : navigator.userAgent;
}

/**
 * Safari (macOS, iOS) and every browser on iPhone/iPad cannot use the print service.
 * Outlets use Chrome or Edge on the till.
 */
export function isUnsupportedBrowser(ua: string = userAgent()): boolean {
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  const isSafari = /Safari\//.test(ua) && !/(Chrome|Chromium|CriOS|Edg|EdgiOS|OPR|FxiOS|Firefox|SamsungBrowser)\//.test(ua);
  return isSafari;
}

/** The only site the release print service accepts prints from. */
export const TPS_WEB_ORIGIN = "https://billing.dhaba.ai";

function currentOrigin(): string {
  try {
    return typeof location === "undefined" ? "" : location.origin;
  } catch {
    return "";
  }
}

/** billing.dhaba.ai, or a local dev server (a dev build of the service accepts localhost). */
export function isSupportedOrigin(origin: string = currentOrigin()): boolean {
  if (origin === TPS_WEB_ORIGIN) return true;
  try {
    const { protocol, hostname } = new URL(origin);
    return (protocol === "http:" || protocol === "https:") && (hostname === "localhost" || hostname === "127.0.0.1");
  } catch {
    return false;
  }
}

/** Why this page can't use the print service at all, before asking it anything (or null). */
export function environmentProblem(ua: string = userAgent(), origin: string = currentOrigin()): TpsProblem | null {
  if (isUnsupportedBrowser(ua)) return "unsupported-browser";
  if (!isSupportedOrigin(origin)) return "wrong-origin";
  return null;
}

/** The state of Chrome's local-network ("loopback-network") permission for this site. */
export async function queryLoopbackPermission(): Promise<LoopbackPermission> {
  const permissions = typeof navigator === "undefined" ? undefined : navigator.permissions;
  if (!permissions?.query) return "unknown";
  for (const name of LOOPBACK_PERMISSION_NAMES) {
    try {
      const status = await permissions.query({ name } as unknown as PermissionDescriptor);
      if (status.state === "granted" || status.state === "denied" || status.state === "prompt") return status.state;
    } catch {
      // This browser doesn't know the name; try the next one.
    }
  }
  return "unknown";
}

/** Best guess at why the service could not be reached. */
export async function diagnoseUnreachable(): Promise<TpsProblem> {
  const environment = environmentProblem();
  if (environment) return environment;
  const permission = await queryLoopbackPermission();
  if (permission === "denied") return "permission-denied";
  if (permission === "prompt") return "permission-prompt";
  return "not-running";
}

export interface TpsProblemAdvice {
  title: string;
  body: string;
  /** Point the person at the Downloads page. */
  showDownloads: boolean;
}

export function tpsProblemAdvice(problem: TpsProblem): TpsProblemAdvice {
  switch (problem) {
    case "unsupported-browser":
      return {
        title: "This browser can't print",
        body: "Printing bills and KOTs needs Google Chrome or Microsoft Edge on the computer the printers are connected to. Safari is not supported.",
        showDownloads: false,
      };
    case "wrong-origin":
      return {
        title: "Printing works only on billing.dhaba.ai",
        body: "The print service on this computer only accepts prints from https://billing.dhaba.ai. Open Dhaba.ai at billing.dhaba.ai in Chrome or Edge to print bills and KOTs.",
        showDownloads: false,
      };
    case "permission-denied":
      return {
        title: "The browser is blocking the print service",
        body: "This site is not allowed to reach devices on this computer. Click the icon to the left of the address bar, open Site settings, set \"Local network access\" to Allow, then reload the page.",
        showDownloads: false,
      };
    case "permission-prompt":
      return {
        title: "Allow local network access",
        body: "The browser asks whether this site may connect to devices on your local network. Click Allow — the print service runs on this computer. If no prompt appears, the print service is not running: start Dhaba Print Service from the Start menu, or install it from Downloads.",
        showDownloads: true,
      };
    case "not-running":
      return {
        title: "Print service not found",
        body: "Dhaba Print Service must be running on this computer to print. Start it from the Start menu, or install it from Downloads. An older Thermal Print Service no longer works with Dhaba.ai — install the new version.",
        showDownloads: true,
      };
  }
}
